// The `digest` header wire shape — one projection, consumed by
// `GET /v1/digests`, `GET /v1/digests/:id` and the MCP `digest` verb's
// `list`/`get`.
//
// Header fields only. `rendered_html` is NOT part of it: the archive list
// deliberately omits the stored HTML. The detail body is `DigestDetailResource`
// below — this schema EXTENDED by that one key, rather than this resource
// carrying a field the list never sends.
//
// The two copies this replaces (`projectDigest` in
// `apps/api/src/routes/digests.ts`, `maskDigest` in
// `packages/mcp/src/army/digest.ts`) spelled the same three nullable timestamps
// two ways — `?.toISOString() ?? null` against `? .toISOString() : null`. Over a
// `Date | null` those agree on every input (a Date object is never falsy), so
// this is a de-duplication and not a behaviour change; the golden test asserts
// it over both a fully-null and a fully-populated sample.

import { z } from 'zod';
import { defineResource } from './resource';

/** The camelCase shape the projection reads. A digest DETAIL row satisfies it too. */
export type DigestEntity = {
  id: string;
  periodStart: Date;
  periodEnd: Date;
  subject: string | null;
  sentAt: Date | null;
  deliveredAt: Date | null;
  openedAt: Date | null;
};

/** The published body. Field names and nullability are the existing contract. */
export const DigestWireSchema = z.object({
  id: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  subject: z.string().nullable(),
  sent_at: z.string().nullable(),
  delivered_at: z.string().nullable(),
  opened_at: z.string().nullable(),
});

/** What a client should type against for a digest header body. */
export type DigestWire = z.infer<typeof DigestWireSchema>;

export const DigestResource = defineResource<DigestEntity, typeof DigestWireSchema>({
  schema: DigestWireSchema,
  serialize: (d) => ({
    id: d.id,
    period_start: d.periodStart.toISOString(),
    period_end: d.periodEnd.toISOString(),
    subject: d.subject,
    sent_at: d.sentAt ? d.sentAt.toISOString() : null,
    delivered_at: d.deliveredAt ? d.deliveredAt.toISOString() : null,
    opened_at: d.openedAt ? d.openedAt.toISOString() : null,
  }),
});

/**
 * The camelCase shape the DETAIL projection reads — the header entity plus the
 * stored reading-view HTML.
 */
export type DigestDetailEntity = DigestEntity & { renderedHtml: string };

/**
 * The detail body: the header, plus the one field the archive list omits.
 *
 * Built by EXTENDING {@link DigestWireSchema} rather than by restating seven
 * keys, so a change to the header cannot leave the detail behind — the failure
 * the three hand-written copies of every body had no way to state.
 */
export const DigestDetailWireSchema = DigestWireSchema.extend({
  /** The stored digest HTML. Never null: a digest row always carries its render. */
  rendered_html: z.string(),
});

/** What a client should type against for a digest detail body. */
export type DigestDetailWire = z.infer<typeof DigestDetailWireSchema>;

/**
 * The detail resource, consumed by `GET /v1/digests/:id` and the MCP `digest`
 * verb's `get`.
 *
 * BOTH doors composed this body by hand until now — `projectDigestDetail`
 * (`apps/api/src/routes/digests.ts`) and an inline object literal in
 * `packages/mcp/src/army/digest.ts` — each spreading `DigestResource.serialize`
 * and appending `rendered_html`. Identical today, and nothing said they had to
 * stay that way: the MCP copy was inside a `structuredResult` call, so it had no
 * declared return type at all.
 *
 * It exists as a resource rather than as a spread at two call sites because
 * `apps/api/src/openapi.ts` publishes bodies only through a route's `response:
 * { kind: 'entity' }`, whose rule is that the body IS a resource exactly — that file names
 * this operation as one of the two it could not publish, "until the addition is
 * itself a resource". This is that addition.
 */
export const DigestDetailResource = defineResource<
  DigestDetailEntity,
  typeof DigestDetailWireSchema
>({
  schema: DigestDetailWireSchema,
  serialize: (d) => ({ ...DigestResource.serialize(d), rendered_html: d.renderedHtml }),
});
